import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import SendbirdChannelSettings from '@sendbird/uikit-react/ChannelSettings';
import { useChannelSettings } from '@sendbird/uikit-react/ChannelSettings/context';
import { MemberList } from '@sendbird/uikit-react/ChannelSettings/components/ChannelSettingsUI';
// @ts-ignore
import { MenuItem, MenuItemAction } from '@sendbird/uikit-react/ChannelSettings/components/ChannelSettingsMenuItem';
// @ts-ignore
import UserListItem from '@sendbird/uikit-react/ui/UserListItem';
// @ts-ignore
import Icon, { IconTypes, IconColors } from '@sendbird/uikit-react/ui/Icon';
// @ts-ignore
import Badge from '@sendbird/uikit-react/ui/Badge';
import { useSendbird } from '@sendbird/uikit-react';
import type { UserListItemProps } from '@sendbird/uikit-react/types/ui/UserListItem';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { useIntl } from 'react-intl';
import { currentCompanyIdSelector } from 'domain/documents';
import { Apps } from 'domain/env/helpers';
import TooltipForButton from 'components/mui/Tooltip/TooltipForButton';
import { resolveChannelContext } from '../../helpers';
import InviteChannelModal from '../InviteChannelModal';
import { AddMembersBox } from './StyledComponents';

interface ModerationPanelProps {
  membersOpen: boolean;
  setMembersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Signals a membership change (invite) so the parent can remount and refresh the profile title.
  onMembersChanged: () => void;
}

const ModerationPanel: React.FC<ModerationPanelProps> = ({ membersOpen, setMembersOpen, onMembersChanged }) => {
  const { state } = useChannelSettings();
  const { channel } = state;
  const { state: sendbirdState } = useSendbird();
  const currentUserId = sendbirdState.config.userId;
  const currentCompanyId = useSelector(currentCompanyIdSelector);
  const { formatMessage } = useIntl();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const isOperator = channel?.myRole === 'operator';

  const ctx = channel ? resolveChannelContext(channel) : null;
  const isInviteAllowed = !ctx
    ? true
    : ctx.companyId
      ? currentCompanyId === ctx.companyId
      : !currentCompanyId && ctx.app === Apps.AP;

  const handleRemove = useCallback(async (userId: string) => {
    if (!channel) return;
    setRemovingId(userId);
    try {
      await (channel as GroupChannel).banUserWithUserId(userId);
      await (channel as GroupChannel).unbanUserWithUserId(userId);
    } catch (err) {
      console.error('[ChannelSettings] Failed to remove member:', err);
    } finally {
      setRemovingId(null);
    }
  }, [channel]);

  const renderUserListItem = useCallback(({ user, ...rest }: UserListItemProps & { index: number }) => {
    const isCurrentUser = user.userId === currentUserId;
    return (
      <UserListItem
        {...rest}
        user={user}
        renderListItemMenu={
          isCurrentUser
            ? () => null
            : () => (
                <TooltipForButton
                  t={{ id: 'chat.channel.removeMember', defaultMessage: 'Remove from chat' }}
                  slotProps={{ popper: { sx: { zIndex: 1700 } } }}
                >
                  <IconButton
                    size="small"
                    color="error"
                    disabled={removingId === user.userId}
                    onClick={() => handleRemove(user.userId)}
                    aria-label={formatMessage({ id: 'chat.channel.removeMember', defaultMessage: 'Remove from chat' })}
                  >
                    <PersonRemoveOutlinedIcon fontSize="small" />
                  </IconButton>
                </TooltipForButton>
              )
        }
      />
    );
  }, [currentUserId, removingId, handleRemove, formatMessage]);

  return (
    <div className="sendbird-channel-settings__members-list">
      <div className="sendbird-channel-settings__operator">
        <MenuItem
          renderLeft={() => (
            <Icon
              type={IconTypes.MEMBERS}
              fillColor={IconColors.PRIMARY}
              width={24}
              height={24}
              className="sendbird-channel-settings__accordion-icon"
            />
          )}
          renderMiddle={() => (
            <span className="sendbird-label sendbird-label--subtitle-1 sendbird-label--color-onbackground-1">
              {formatMessage({ id: 'chat.channel.members', defaultMessage: 'Members' })}
            </span>
          )}
          renderRight={({ useAccordion, accordionOpened }: { useAccordion: boolean; accordionOpened: boolean }) => (
            <>
              <div className="sendbird-channel-settings__members">
                <Badge count={channel?.memberCount ?? ''} />
              </div>
              <MenuItemAction useAccordion={useAccordion} accordionOpened={accordionOpened} />
            </>
          )}
          renderAccordion={() => (
            <Box
              sx={{
                '& .sendbird-channel-settings-accordion__footer': { display: 'none' },
              }}
            >
              <MemberList
                memberListQueryParams={{ limit: 12 }}
                renderUserListItem={isOperator ? renderUserListItem : undefined}
              />
              <AddMembersBox>
                <TooltipForButton
                  t={
                    isInviteAllowed
                      ? undefined
                      : { id: 'chat.channel.inviteNotAllowedHint', defaultMessage: "Navigate to the chat's company context to invite relevant users" }
                  }
                >
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<PersonAddOutlinedIcon fontSize="small" />}
                    disabled={!isInviteAllowed}
                    onClick={() => setShowInviteModal(true)}
                  >
                    {formatMessage({ id: 'chat.channel.inviteMember', defaultMessage: 'Invite member' })}
                  </Button>
                </TooltipForButton>
              </AddMembersBox>
              {showInviteModal && channel && (
                <InviteChannelModal
                  channel={channel as GroupChannel}
                  onCancel={() => setShowInviteModal(false)}
                  onInvited={onMembersChanged}
                />
              )}
            </Box>
          )}
          accordionOpened={membersOpen}
          setAccordionOpened={() => setMembersOpen((prev) => !prev)}
        />
      </div>
    </div>
  );
};

interface CustomChannelSettingsProps {
  channelUrl: string;
  onCloseClick?: () => void;
}

const CustomChannelSettings: React.FC<CustomChannelSettingsProps> = (props) => {
  const [membersOpen, setMembersOpen] = useState(false);
  // Sendbird's ChannelProfile memoizes its title on `joinedMemberCount`, which doesn't change on
  // an invite (invited != joined), so the panel title stayed stale until reopened (DA-19568).
  // Bumping this key remounts the subtree — the same refetch reopening does. `membersOpen` lives
  // above the boundary, so the members accordion stays expanded across the refresh.
  const [profileVersion, setProfileVersion] = useState(0);

  return (
    <SendbirdChannelSettings
      key={profileVersion}
      {...props}
      renderModerationPanel={() => (
        <ModerationPanel
          membersOpen={membersOpen}
          setMembersOpen={setMembersOpen}
          onMembersChanged={() => setProfileVersion((v) => v + 1)}
        />
      )}
      renderLeaveChannel={() => <></>}
    />
  );
};

export default CustomChannelSettings;
